import React, { useState } from "react";
import { IoMdCloseCircle } from "react-icons/io";
import { IoHelpCircleOutline } from "react-icons/io5";
import { FaWrench } from "react-icons/fa6";
import { WrittenFeedbackPopup } from "./FeedbackComponents";
import { callWithAuth, postWordError } from "@/api/Api";
import { useTrackClickHintView } from "../hooks/posthog/useTrackClickHintView";

// Feedback mode types
type FeedbackMode = "none" | "report" | "text" | "thanks";

interface ReportErrorProps {
  lessonId: number;
  videoWordId: number;
  targetWord: string;
  clickHelp?: string;
  isDisplayHelp?: boolean;
  className?: string;
  wrenchPadding?: string;
  leftControlClassName?: string;
  rightControlClassName?: string;
}

const ReportError: React.FC<ReportErrorProps> = ({
  lessonId,
  videoWordId,
  targetWord,
  clickHelp = "",
  isDisplayHelp = false,
  className = "",
  wrenchPadding = "m-[1px]",
  leftControlClassName = "",
  rightControlClassName = "",
}) => {
  const [isClickHintView, setIsClickHintView] = useState(false);
  const [feedbackMode, setFeedbackMode] = useState<FeedbackMode>("none");
  const [textFeedback, setTextFeedback] = useState("");
  const [showFeedbackPopup, setShowFeedbackPopup] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const trackClickHintView = useTrackClickHintView();

  function handleFeedbackPopupClose() {
    setShowFeedbackPopup(false);
    setFeedbackMode("none");
    setIsClickHintView(false);
  }

  const handleWrenchClick = () => {
    // Toggle feedback mode - if already in report mode, close it
    console.log("handleWrenchClick", feedbackMode);
    if (feedbackMode === "report") {
      setFeedbackMode("none");
      setIsClickHintView(false);
    } else {
      setIsClickHintView(true);
      setFeedbackMode("report");
    }
  };

  const reportIncorrectWord = () => {
    // API call to report incorrect word/activity
    console.log(`Reporting incorrect ${videoWordId === 0 ? 'activity' : 'word'}: ${targetWord}`);
    try {
      callWithAuth(postWordError, {
        videoWordId: videoWordId,
        feedback: "",
        lessonId: lessonId,
      });
    } catch (error) {
      console.error("Error reporting incorrect word/activity", error);
    }
    setFeedbackMode("thanks");
  };

  const submitTextFeedback = (feedback: string) => {
    // API call to submit text feedback
    console.log(`Submitting feedback: ${textFeedback || feedback}`, {
      item: videoWordId === 0 ? 'activity' : 'word',
      target: targetWord,
    });
    try {
      callWithAuth(postWordError, {
        videoWordId: videoWordId,
        feedback: feedback,
        lessonId: lessonId,
      });
    } catch (error) {
      console.error("Error submitting feedback", error);
    }
    setFeedbackSubmitted(true);
  };

  const closeFeedbackPopup = () => {
    setShowFeedbackPopup(false);
    setFeedbackSubmitted(false);
    setFeedbackMode("none");
    setIsClickHintView(false);
  };

  const handleClickHintView = () => {
    setIsClickHintView(true);
    trackClickHintView();
  };

  // Render the appropriate feedback view based on the mode
  const renderFeedbackView = () => {
    const isActivity = videoWordId === 0;
    const itemDisplayName = isActivity ? "this activity" : "this word";

    switch (feedbackMode) {
      case "report":
        return (
          <div className="flex flex-col items-center justify-center h-full z-30">
            <p className="text-center mb-4 font-medium">
              Would you like to report {itemDisplayName} as incorrect?
            </p>
            <div className="flex space-x-3 justify-center">
              <button
                onClick={reportIncorrectWord}
                className="px-4 py-2 bg-[var(--color-green)] text-white text-sm rounded-xl hover:bg-[var(--color-green)] transition"
              >
                Yes
              </button>
              <button
                onClick={() => {
                  setFeedbackMode("none");
                  setIsClickHintView(false);
                }}
                className="px-4 py-2 bg-[var(--color-red)] text-white text-sm rounded-xl hover:bg-[var(--color-red)] transition"
              >
                No
              </button>
              <button
                onClick={() => {
                  setShowFeedbackPopup(true);
                }}
                className="px-4 py-2 bg-[var(--color-blue)] text-white text-sm rounded-xl hover:bg-[var(--color-blue)] transition"
              >
                Other feedback
              </button>
            </div>
          </div>
        );
      case "text":
        return (
          <div className="flex flex-col items-center justify-center h-full z-30">
            <p className="text-center mb-4 font-medium">
              What feedback would you like to provide?
            </p>
            <textarea
              className="w-full p-3 border border-gray-300 rounded-md mb-4"
              rows={4}
              value={textFeedback}
              onChange={(e) => setTextFeedback(e.target.value)}
              placeholder="Your feedback helps us improve..."
            />
            <button
              onClick={() => {
                setShowFeedbackPopup(true);
              }}
              className="px-5 py-2 bg-[var(--color-blue)] text-white rounded-md hover:bg-[var(--color-blue)] transition"
            >
              Submit
            </button>
          </div>
        );
      case "thanks":
        return (
          <div className="flex flex-col items-center justify-center h-full z-30">
            <p className="text-center text-lg font-medium mb-2">
              Thank you for your feedback!
            </p>
            <p className="text-center text-gray-600 mb-4">
              Your input helps make Parrot better.
            </p>
            <button
              onClick={() => {
                setShowFeedbackPopup(true);
              }}
              className="px-4 py-2 bg-[var(--color-blue)] text-white text-sm rounded-xl hover:bg-[var(--color-blue)] transition"
            >
              Share more feedback
            </button>
          </div>
        );
      default:
        return <div className="text-center h-3/4 p-2">{clickHelp}</div>;
    }
  };

  return (
    <>
      {isClickHintView && (
        <div
          className={`absolute inset-2 m-auto z-0 flex flex-col justify-center items-center rounded-xl bg-[#fafafa] h-full ${className}`}
        >
          {renderFeedbackView()}
        </div>
      )}

      {/* Left bottom corner controls */}
      <div
        className={`absolute bottom-1 left-[8px] z-20 ${leftControlClassName}`}
        style={{
          marginLeft: "4px",
          marginBottom: "4px",
        }}
      >
        {isClickHintView ? (
          <IoMdCloseCircle
            onClick={closeFeedbackPopup}
            className="w-6 h-6 hover:cursor-pointer"
          />
        ) : (
          <>
            {clickHelp !== "" && !isDisplayHelp ? (
              <IoHelpCircleOutline
                onClick={handleClickHintView}
                className={`w-6 h-6 hover:cursor-pointer ${wrenchPadding}`}
              />
            ) : (
              // Show wrench if there's no click help
              !isDisplayHelp && (
                <FaWrench
                  onClick={handleWrenchClick}
                  className={`w-5 h-5 hover:cursor-pointer ${wrenchPadding}`}
                />
              )
            )}
          </>
        )}
      </div>

      {/* Right bottom corner controls */}
      <div
        className={`absolute bottom-1 right-[8px] z-20 ${rightControlClassName}`}
        style={{
          marginRight: "4px",
          marginBottom: "4px",
        }}
      >
        {isClickHintView && clickHelp != "" && (
          <FaWrench
            onClick={handleWrenchClick}
            className="w-5 h-5 hover:cursor-pointer"
          />
        )}
      </div>

      <WrittenFeedbackPopup
        isOpen={showFeedbackPopup}
        onSubmit={submitTextFeedback}
        onCancel={handleFeedbackPopupClose}
        onClose={closeFeedbackPopup}
        showThankYou={feedbackSubmitted}
        thankYouMessage="Thank you for your feedback! Your input helps make Parrot better."
        title="What feedback would you like to provide?"
        placeholderText="Tell us how we can improve..."
      />
    </>
  );
};

export default ReportError; 